import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType: string | null;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
}

interface NetworkStatusHook extends NetworkStatus {
  checkConnectivity: () => Promise<boolean>;
  retryConnection: () => void;
}

export function useNetworkStatus(): NetworkStatusHook {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlowConnection: false,
    connectionType: null,
    effectiveType: null,
    downlink: null,
    rtt: null,
  });

  const updateNetworkInfo = useCallback(() => {
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      const isSlowConnection = 
        connection.effectiveType === 'slow-2g' || 
        connection.effectiveType === '2g' ||
        (connection.downlink && connection.downlink < 1.5);

      setNetworkStatus(prev => ({
        ...prev,
        isSlowConnection,
        connectionType: connection.type || null,
        effectiveType: connection.effectiveType || null,
        downlink: connection.downlink || null,
        rtt: connection.rtt || null,
      }));
    }
  }, []);

  const handleOnline = useCallback(() => {
    setNetworkStatus(prev => ({ ...prev, isOnline: true }));
    updateNetworkInfo();
  }, [updateNetworkInfo]);

  const handleOffline = useCallback(() => {
    setNetworkStatus(prev => ({ ...prev, isOnline: false }));
  }, []);

  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    try {
      // Try to fetch a small resource to test connectivity
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });
      
      const isConnected = response.ok;
      setNetworkStatus(prev => ({ ...prev, isOnline: isConnected }));
      
      if (isConnected) {
        updateNetworkInfo();
      }
      
      return isConnected;
    } catch (error) {
      console.warn('Connectivity check failed:', error);
      setNetworkStatus(prev => ({ ...prev, isOnline: false }));
      return false;
    }
  }, [updateNetworkInfo]);

  const retryConnection = useCallback(() => {
    checkConnectivity();
  }, [checkConnectivity]);

  useEffect(() => {
    // Initial network info update
    updateNetworkInfo();

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for connection changes
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', updateNetworkInfo);
    }

    // Periodic connectivity check (every 30 seconds when online)
    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        checkConnectivity();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection) {
        connection.removeEventListener('change', updateNetworkInfo);
      }
      
      clearInterval(intervalId);
    };
  }, [handleOnline, handleOffline, updateNetworkInfo, checkConnectivity]);

  return {
    ...networkStatus,
    checkConnectivity,
    retryConnection,
  };
}

export default useNetworkStatus;